import { FlexibleConnectedPositionStrategy, GlobalPositionStrategy, OverlayContainer } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';

/**
 * Workspace Isolation Strategy — confines all CDK overlays to `.main-app-content`.
 *
 * Two CDK internals bypass ViewportRuler and are monkey-patched here:
 *  1. `FlexibleConnectedPositionStrategy._getNarrowedViewportRect()` reads
 *     `document.documentElement.clientWidth` directly (affects menus, selects).
 *  2. `GlobalPositionStrategy.apply()` only sets flex-alignment on the wrapper;
 *     size/position come from CDK prebuilt CSS (affects dialogs).
 *
 * ⚠️ Never add `!important` CSS for `.cdk-global-overlay-wrapper`.
 *    That would override the inline styles set in Patch 2.
 *
 * Remaining constraints (overflow, pane min/max sizes) are in styles.scss.
 */

type ViewportRect = { top: number; left: number; right: number; bottom: number; width: number; height: number };

/** Private fields of FlexibleConnectedPositionStrategy accessed by Patch 1. */
interface FlexibleStrategyInternal {
  readonly _viewportRuler?: ViewportRuler;
  readonly _overlayRef?: { hostElement: HTMLElement };
  _getViewportMarginStart?(): number;
  _getViewportMarginEnd?(): number;
  _getViewportMarginTop?(): number;
  _getViewportMarginBottom?(): number;
}

/** Private fields of GlobalPositionStrategy accessed by Patch 2. */
interface GlobalStrategyInternal {
  readonly _overlayRef: { hasAttached(): boolean; hostElement: HTMLElement };
}

interface PatchableFlexible {
  _getNarrowedViewportRect(this: FlexibleStrategyInternal): ViewportRect;
}

interface PatchableGlobal {
  apply(this: GlobalStrategyInternal): void;
}

// ─── Shared workspace element cache ──────────────────────────────────────────

let _cachedWorkspace: HTMLElement = null;

function getWorkspace(doc: Document): HTMLElement {
  if (_cachedWorkspace?.isConnected) return _cachedWorkspace;
  _cachedWorkspace = doc.querySelector<HTMLElement>('.main-app-content');
  return _cachedWorkspace;
}

// ─── Patch 1: FlexibleConnectedPositionStrategy._getNarrowedViewportRect ─────

let __patchedFlexible = false;

function patchFlexiblePositionStrategy(doc: Document): void {
  if (__patchedFlexible) return;
  __patchedFlexible = true;

  const proto = FlexibleConnectedPositionStrategy.prototype as unknown as PatchableFlexible;
  const original = proto._getNarrowedViewportRect;

  proto._getNarrowedViewportRect = function (this: FlexibleStrategyInternal): ViewportRect {
    const workspace = getWorkspace(doc);
    if (!workspace) return original.call(this);

    // If this overlay's host is NOT inside .main-app-content (e.g. it's in the AI sidebar),
    // fall back to CDK's default so the menu positions relative to the real viewport.
    const hostEl = this._overlayRef?.hostElement;
    if (hostEl && !workspace.contains(hostEl)) {
      return original.call(this);
    }

    const rect = workspace.getBoundingClientRect();
    const scroll = this._viewportRuler?.getViewportScrollPosition() ?? { top: 0, left: 0 };
    const ms = this._getViewportMarginStart?.() ?? 0;
    const me = this._getViewportMarginEnd?.() ?? 0;
    const mt = this._getViewportMarginTop?.() ?? 0;
    const mb = this._getViewportMarginBottom?.() ?? 0;

    return {
      top: scroll.top + rect.top + mt,
      left: scroll.left + rect.left + ms,
      right: scroll.left + rect.right - me,
      bottom: scroll.top + rect.bottom - mb,
      width: rect.width - ms - me,
      height: rect.height - mt - mb,
    };
  };
}

// ─── Patch 2: GlobalPositionStrategy.apply ───────────────────────────────────

let __patchedGlobal = false;

function patchGlobalPositionStrategy(doc: Document): void {
  if (__patchedGlobal) return;
  __patchedGlobal = true;

  const proto = GlobalPositionStrategy.prototype as unknown as PatchableGlobal;
  const original = proto.apply;

  proto.apply = function (this: GlobalStrategyInternal): void {
    original.call(this);

    if (!this._overlayRef?.hasAttached()) return;
    if (!getWorkspace(doc)) return;

    const wrapper = this._overlayRef.hostElement;
    wrapper.style.position = 'fixed';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.overflow = 'hidden';
  };
}

// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AppCdkOverlayContainer extends OverlayContainer {
  private readonly doc = inject(DOCUMENT);
  private transplantDone = false;

  constructor() {
    super(inject(DOCUMENT));
    patchFlexiblePositionStrategy(this.doc);
    patchGlobalPositionStrategy(this.doc);
  }

  override getContainerElement(): HTMLElement {
    if (!this._containerElement) {
      this._createContainer();
    }

    if (!this.transplantDone) {
      const workspace = getWorkspace(this.doc);
      if (workspace && this._containerElement.parentElement !== workspace) {
        workspace.appendChild(this._containerElement);
        this.transplantDone = true;
      }
    }

    return this._containerElement;
  }

  protected override _createContainer(): void {
    const container = this.doc.createElement('div');
    container.classList.add('cdk-overlay-container');

    const workspace = getWorkspace(this.doc);
    if (workspace) {
      workspace.appendChild(container);
      this.transplantDone = true;
    } else {
      this.doc.body.appendChild(container);
    }

    this._containerElement = container;
  }
}

// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AppCdkViewportRuler extends ViewportRuler {
  private readonly doc = inject(DOCUMENT);

  constructor() {
    super(inject(Platform), inject(NgZone), inject(DOCUMENT));
  }

  private workspaceRect(): DOMRect {
    return getWorkspace(this.doc)?.getBoundingClientRect() ?? null;
  }

  override getViewportSize(): Readonly<{ width: number; height: number }> {
    const rect = this.workspaceRect();
    return rect ? { width: rect.width, height: rect.height } : super.getViewportSize();
  }

  override getViewportRect(): ViewportRect {
    const rect = this.workspaceRect();
    return rect
      ? {
          top: rect.top,
          left: rect.left,
          bottom: rect.bottom,
          right: rect.right,
          width: rect.width,
          height: rect.height,
        }
      : super.getViewportRect();
  }

  override getViewportScrollPosition(): { top: number; left: number } {
    const workspace = getWorkspace(this.doc);
    return workspace ? { top: workspace.scrollTop, left: workspace.scrollLeft } : super.getViewportScrollPosition();
  }
}
